import type { ShipmentResponse, TrackingData, Shipment, TrackingEvent } from '@/types/Models/Shipment'
import { setOptions } from '@googlemaps/js-api-loader'
import { defineStore } from 'pinia'
import { SEARCH_PAGE } from '@/consts/Router'
import { none } from '@/helpers/Utils'
import router from '@/router'
import TrackingProxy from '@/proxies/TrackingProxy'

const createDefaultTrackingInfo = (): TrackingData => ({
  id: '',
  estimatedCost: null,
  service: '',
  senderReference: null,
  weight: 0,
  height: 0,
  length: 0,
  width: 0,
  deliveryType: null,
  distributionChannel: '',
  sender: {
    id: '',
    name: '',
    firstName: '',
    lastName: '',
    shortName: '',
    company: '',
    countryCode: '',
    postalCode: '',
    country: '',
    region: null,
    city: '',
    email: '',
  },
  recipient: {
    id: '',
    type: '',
    firstName: '',
    lastName: null,
    fullName: '',
    company: null,
    countryCode: '',
    country: '',
    postalCode: '',
    region: null,
    city: '',
    addressLine: '',
    addressLine2: null,
    phoneNumber: '',
    email: '',
  },
  shipments: [],
  servicePoints: [],
  pickupPoint: null,
  forwardedToServicePoint: false,
  externalTrackingLink: null,
  partnerName: null,
})

const createDefaultState = () => ({
  trackingInfo: createDefaultTrackingInfo(),
  proofOfDelivery: '',
  // Carrier event texts in the page language (original -> translated), filled by translateEventTexts
  eventTexts: {} as Record<string, string>,
})

let googleMapsInitialized = false

export const useTrackingStore = defineStore('tracking', {
  state: () => createDefaultState(),
  getters: {
    primaryShipment: (state): Shipment | null =>
      state.trackingInfo?.shipments?.length
        ? [...state.trackingInfo.shipments].sort((a, b) => b.runningNumber - a.runningNumber)[0] || null
        : null,
    secondaryShipment: (state): Shipment | null => {
      const shipments = state.trackingInfo?.shipments || []
      if (shipments.length < 2) return null
      const sorted = [...shipments].sort((a, b) => b.runningNumber - a.runningNumber)
      const primary = sorted[0]
      return sorted.find((s) => s !== primary) || null
    },
    trackingEvents: (state): TrackingEvent[] =>
      !state.trackingInfo?.shipments?.length
        ? []
        : [
            ...(state.trackingInfo.shipments[0]?.trackingEvents || []),
            ...(state.trackingInfo.shipments[1]?.trackingEvents || []),
          ].sort(
            (a: TrackingEvent, b: TrackingEvent) => new Date(b.eventTime).getTime() - new Date(a.eventTime).getTime(),
          ),
    eventText:
      (state) =>
      (text?: string | null): string =>
        (text && state.eventTexts[text]) || text || '',
  },
  actions: {
    // Carriers write in different languages; the tracking assistant translates them into the page language
    // (cached there). Only where the assistant is configured; otherwise the carrier's own text is shown.
    async translateEventTexts(lang: string): Promise<void> {
      const assistantUrl = import.meta.env.VITE_ASSISTANT_URL
      const texts = [...new Set(this.trackingEvents.map((e) => e.description).filter(Boolean))] as string[]
      this.eventTexts = {}
      if (!assistantUrl || !texts.length) return
      try {
        const response = await fetch(new URL('/api/translate', assistantUrl).toString(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lang, texts }),
        })
        const body = (await response.json()) as { texts?: string[] }
        const translated: Record<string, string> = {}
        texts.forEach((text, i) => {
          if (body.texts?.[i]) translated[text] = body.texts[i]
        })
        this.eventTexts = translated
      } catch {
        // keep the carrier's own texts
      }
    },
    initGoogleMaps(apiKey: string): void {
      if (googleMapsInitialized) return

      // Configure API key and version for the loader (cast to any to satisfy TS in current package types)
      setOptions({ key: apiKey, version: 'weekly' } as any)
      googleMapsInitialized = true
    },
    fetch(number: string): void {
      new TrackingProxy()
        .getTrackingData(number)
        .then((response: ShipmentResponse) => {
          this.trackingInfo = response.data
        })
        .catch(async () => {
          await router.push({ name: SEARCH_PAGE }).then(none)
        })
    },
    fetchProofOfDelivery(number: string): void {
      this.proofOfDelivery = ''
      new TrackingProxy()
        .getProofOfDelivery(number)
        .then((response: any) => {
          this.proofOfDelivery = String(response?.url || '')
        })
        .catch(none)
    },
    markAsRead(internalId: string): void {
      fetch(import.meta.env.VITE_MAILER_API_LOCATION + '/v2/webhook/sms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ internalId }),
      })
        .then(none)
        .catch(none)
    },
  },
})

export type TrackingStore = ReturnType<typeof useTrackingStore>
